import { React, useState } from "react";
import { isNullOrUndefined } from "utils/validationUtil";
import { parseApiError } from "app/services/api.error.parser";
import { handleAxiosError } from "app/services/app.error.handler";
import { adminInitiateReset } from "app/services/forgotPassword.service";

import { Card, Button, TextField, Alert, IconButton, Collapse } from "@mui/material";
import { Close as CloseIcon } from "components/icons";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ConfirmDialog } from "app/components/ConfirmDialog";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * ADMIN-only screen: initiate a password reset for a user whose own email may be
 * unreachable (e.g. a synthetic admin login id). The OTP is generated for the target
 * user but delivered to a reachable "send code to" address (optionally CC'd). The target
 * user then completes the normal OTP + reset flow themselves.
 */
export const AdminResetPasswordView = () => {
  const [targetEmail, setTargetEmail] = useState("");
  const [deliverToEmail, setDeliverToEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");

  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("info");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const showAlert = (msg, sev) => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  };

  const clearForm = () => {
    setTargetEmail("");
    setDeliverToEmail("");
    setCcEmail("");
  };

  const validateAndConfirm = (e) => {
    e.preventDefault();

    if (isNullOrUndefined(targetEmail) || !EMAIL_REGEX.test(targetEmail.trim())) {
      showAlert("Please enter a valid target user email.", "warning");
      return;
    }
    if (isNullOrUndefined(deliverToEmail) || !EMAIL_REGEX.test(deliverToEmail.trim())) {
      showAlert("Please enter a valid 'send code to' email.", "warning");
      return;
    }
    if (ccEmail && ccEmail.trim() !== "" && !EMAIL_REGEX.test(ccEmail.trim())) {
      showAlert("The CC email is not a valid email address.", "warning");
      return;
    }
    setConfirmOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const response = await adminInitiateReset(targetEmail.trim(), deliverToEmail.trim(), ccEmail);
      showAlert(response.data?.message || "One-time code sent.", "success");
      clearForm();
    } catch (error) {
      showAlert(parseApiError(error).message, "error");
      handleAxiosError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MDBox sx={{ display: "flex", justifyContent: "center", mt: 5, px: 2 }}>
      <Card sx={{ width: "100%", maxWidth: 520 }}>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Reset Password for User
          </MDTypography>
        </MDBox>

        <MDBox px={3} pt={2}>
          <MDTypography variant="body2" color="text">
            Generate a one-time code for a user and have it delivered to a reachable inbox. The
            user then completes the reset themselves.
          </MDTypography>
        </MDBox>

        <form onSubmit={validateAndConfirm} noValidate>
          <MDBox px={3} pt={2}>
            <MDBox mb={2}>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="target-email"
                label="Target user email"
                name="target-email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="deliver-to-email"
                label="Send code to (reachable email)"
                name="deliver-to-email"
                value={deliverToEmail}
                onChange={(e) => setDeliverToEmail(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                variant="outlined"
                fullWidth
                id="cc-email"
                label="CC (optional)"
                name="cc-email"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
              />
            </MDBox>

            <MDBox mb={1}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send Reset Code"}
              </Button>
            </MDBox>
          </MDBox>
        </form>

        <MDBox px={3} pb={3}>
          <Collapse in={open}>
            <Alert
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setOpen(false)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              severity={severity}
              sx={{ mb: 1 }}
            >
              {message}
            </Alert>
          </Collapse>
        </MDBox>
      </Card>

      <ConfirmDialog
        title="Confirmation Required!"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={submit}
        yesButtonLabel="Send"
        noButtonLabel="Cancel"
      >
        Send a one-time reset code for <strong>{targetEmail}</strong> to{" "}
        <strong>{deliverToEmail}</strong>
        {ccEmail && ccEmail.trim() !== "" ? (
          <>
            {" "}
            (CC <strong>{ccEmail}</strong>)
          </>
        ) : null}
        ?
      </ConfirmDialog>
    </MDBox>
  );
};
